﻿import {
  Briefcase,
  Calendar,
  Heart,
  MapPin,
  Search,
  Trash2,
  Wallet,
} from 'lucide-react';
import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useProviderFavorite } from '../../contexts/ProviderFavoriteContext';
import toast from 'react-hot-toast';
import {
  manproQueryKeys,
  providerOpportunityService,
  type PaginatedResponse,
  type ProviderOpportunityDto,
  type ProviderOpportunityParams,
} from '../../api/manpro';
import {
  getOpportunityCoverImages,
  getOpportunitySourceLabel,
} from '../../lib/manproOpportunityMedia';

const opportunityImages = [
  'https://images.unsplash.com/photo-1541888086925-920eb1f1a1d3?q=80&w=800&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1504307651254-35680f356f12?q=80&w=800&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?q=80&w=800&auto=format&fit=crop',
];

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const getDeadlineNote = (value?: string | null) => {
  if (!value) return 'Deadline belum ditentukan';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Deadline belum ditentukan';

  const diffDays = Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'Berakhir';
  if (diffDays === 0) return 'Hari ini';
  return `${diffDays} hari lagi`;
};

const isPaginatedResponse = (
  payload?: PaginatedResponse<ProviderOpportunityDto> | ProviderOpportunityDto[] | null,
): payload is PaginatedResponse<ProviderOpportunityDto> =>
  Boolean(payload && !Array.isArray(payload) && Array.isArray(payload.data));

const FavoritProvider = () => {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(query.trim());
      setPage(1);
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [query]);

  const queryParams = useMemo<ProviderOpportunityParams>(
    () => ({
      offset: page,
      limit: 20,
      search: debouncedSearch || undefined,
    }),
    [page, debouncedSearch]
  );

  const favoritesQuery = useQuery({
    queryKey: ['manpro', 'provider', 'opportunities', 'favorites', queryParams],
    queryFn: () => providerOpportunityService.getFavoriteOpportunities(queryParams),
  });
  
  const favorites = (isPaginatedResponse(favoritesQuery.data) ? favoritesQuery.data.data : favoritesQuery.data ?? []) as any[];
  const pagination = isPaginatedResponse(favoritesQuery.data) ? favoritesQuery.data : null;
  const currentPage = pagination?.current_page ?? page;
  const lastPage = pagination?.last_page ?? 1;

  const filteredItems = useMemo(
    () =>
      favorites.map((item, index) => ({
        id: item.id,
        title: item.title,
        type: item.procurement_type || item.category || 'Peluang Proyek',
        location: item.location || item.address_detail || '-',
        budget: item.budget_range || 'Budget belum ditentukan',
        deadline: getDeadlineNote(item.bid_deadline || item.deadline),
        deadlineDate: formatDate(item.bid_deadline || item.deadline),
        source: getOpportunitySourceLabel(item),
        image: getOpportunityCoverImages(item)[0] || opportunityImages[index % opportunityImages.length],
      })),
    [favorites],
  );

  const { toggleFavorite, isToggling } = useProviderFavorite();

  return (
    <div className="space-y-6">
      <section>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Proyek Tersimpan</h1>
        <p className="mt-2 text-sm text-slate-500">
          Simpan peluang proyek yang ingin Anda pantau atau review kembali sebelum mengajukan bid.
        </p>
      </section>

      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[
          [String(pagination?.total ?? favorites.length), 'Total favorit', Heart],
          [String(pagination?.total ?? favorites.length), 'Peluang proyek', Briefcase],
        ].map(([value, label, Icon]) => (
          <div key={label as string} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-extrabold text-slate-900">{value as string}</p>
                <p className="mt-1 text-sm font-semibold text-slate-500">{label as string}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-700">
                <Icon size={24} />
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="grid items-start gap-6 lg:grid-cols-1">
        <div className="min-w-0 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Cari peluang proyek favorit..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm font-semibold text-slate-900 outline-none transition placeholder:font-medium placeholder:text-slate-400 focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
              />
            </div>
          </div>

          {favoritesQuery.isLoading && (
            <div className="grid gap-4 md:grid-cols-2">
              {[...Array(4)].map((_, index) => (
                <div
                  key={`skeleton-opportunity-${index}`}
                  className="animate-pulse rounded-2xl border border-slate-200 bg-white p-4"
                >
                  <div className="grid gap-4 lg:grid-cols-[160px_minmax(0,1fr)_180px_120px]">
                    <div className="h-36 w-full rounded-xl bg-slate-200 lg:h-28" />
                    <div className="space-y-3">
                      <div className="h-5 w-24 rounded-lg bg-slate-200" />
                      <div className="h-6 w-3/4 rounded-lg bg-slate-200" />
                      <div className="h-5 w-32 rounded-lg bg-slate-200" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {favoritesQuery.isError && (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-10 text-center">
              <p className="text-sm font-bold text-red-600">Gagal memuat data favorit.</p>
              <button
                onClick={() => favoritesQuery.refetch()}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-extrabold text-white transition hover:bg-red-700"
              >
                Coba Lagi
              </button>
            </div>
          )}

          {!favoritesQuery.isLoading && !favoritesQuery.isError && filteredItems.length > 0 && (
            <div className="grid gap-4">
              {filteredItems.map((item) => (
                <article key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-100 hover:shadow-md">
                  <div className="grid gap-4 lg:grid-cols-[160px_minmax(0,1fr)_180px_120px] lg:items-center">
                    <img src={item.image} alt={item.title} className="h-36 w-full rounded-xl object-cover lg:h-28" />
                    <div className="min-w-0">
                      <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-extrabold text-blue-700">{item.type}</span>
                      <h2 className="mt-3 text-lg font-extrabold text-slate-900">{item.title}</h2>
                      <div className="mt-3 grid gap-2 text-sm text-slate-500 sm:grid-cols-2">
                        <span className="inline-flex items-center gap-2">
                          <MapPin size={16} />
                          {item.location}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <Wallet size={16} />
                          {item.budget}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm">
                      <p className="font-extrabold text-slate-800">Deadline</p>
                      <p className="mt-2 flex items-center gap-2 text-red-500">
                        <Calendar size={15} />
                        {item.deadline}
                      </p>
                      <p className="mt-2 text-xs text-slate-500">{item.deadlineDate} â€¢ {item.source}</p>
                    </div>
                    <div className="grid gap-2">
                      <Link
                        to={`/provider-opportunities/${item.id}`}
                        className="rounded-xl bg-blue-600 px-4 py-2.5 text-center text-sm font-extrabold text-white"
                      >
                        Lihat Detail
                      </Link>
                      <button
                        onClick={() => toggleFavorite(item.id)}
                        disabled={isToggling}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-100 px-4 py-2.5 text-sm font-extrabold text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                        type="button"
                      >
                        <Trash2 size={16} />
                        Hapus
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {!favoritesQuery.isLoading && !favoritesQuery.isError && filteredItems.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <p className="text-sm font-semibold text-slate-400">Proyek tersimpan tidak ditemukan.</p>
              <Link
                to="/provider-opportunities"
                className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-extrabold text-white transition hover:bg-blue-700"
              >
                Cari Proyek
              </Link>
            </div>
          )}

          {lastPage > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
              <button
                onClick={() => setPage((previous) => Math.max(previous - 1, 1))}
                disabled={currentPage <= 1}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                type="button"
              >
                Sebelumnya
              </button>
              {Array.from({ length: lastPage }).slice(0, 7).map((_, index) => {
                const pageNumber = index + 1;

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setPage(pageNumber)}
                    className={`h-10 w-10 rounded-xl text-sm font-extrabold ${
                      currentPage === pageNumber
                        ? 'bg-blue-600 text-white'
                        : 'border border-slate-200 bg-white text-slate-600'
                    }`}
                    type="button"
                  >
                    {pageNumber}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((previous) => Math.min(previous + 1, lastPage))}
                disabled={currentPage >= lastPage}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                type="button"
              >
                Selanjutnya
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default FavoritProvider;

